import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/safeSupabase";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { TrendingUp, Shield, BarChart3 } from "lucide-react";
import tacticalLogo from "@/assets/tactical-tax-logo.png";
import { toast } from 'sonner';
import { sanitizeError } from '@/lib/errorMessages';
import { checkRateLimit, getRetryAfter } from '@/lib/rateLimiter';

type Mode = "login" | "forgot";

export default function Auth() {
  const { user, loading: authLoading, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      navigate(isSuperAdmin ? "/super-admin" : "/", { replace: true });
    }
  }, [user, authLoading, isSuperAdmin, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkRateLimit('auth:login', 5, 60_000)) {
      const wait = getRetryAfter('auth:login', 5, 60_000);
      toast.error('Too many attempts', { description: `Please wait ${wait} seconds before trying again.` });
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      toast.error('Login failed', { description: sanitizeError(error) });
    } else {
      // Navigation handled by the useEffect above once auth state updates
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkRateLimit('auth:forgot', 3, 60_000)) {
      const wait = getRetryAfter('auth:forgot', 3, 60_000);
      toast.error('Too many attempts', { description: `Please wait ${wait} seconds before trying again.` });
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    setLoading(false);
    if (error) {
      toast.error('Error', { description: sanitizeError(error) });
    } else {
      toast.success('Email sent', { description: 'Check your inbox for a password reset link.' });
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left branding panel — white, navy/blue */}
      <div className="hidden lg:flex lg:w-1/2 bg-white border-r border-gray-100 p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            {/* TT icon — sharp corners, no clipping */}
            <img src={tacticalLogo} alt="Tactical Tax Genie" className="h-12 w-12 object-contain" style={{ borderRadius: 0 }} />
            <div>
              <span className="text-xl font-bold tracking-tight" style={{ color: '#2D4B6E' }}>Tactical Tax Genie</span>
            </div>
          </div>
          <p className="text-gray-400 text-sm mt-1">2026 Federal + All 50 States</p>
        </div>

        <div className="space-y-8">
          <h2 className="text-4xl font-bold leading-tight" style={{ color: '#2D4B6E' }}>
            Professional Tax
            <br />
            Planning Platform
          </h2>
          <p className="text-gray-500 text-lg max-w-md">
            Model unlimited scenarios, compare outcomes side-by-side, and optimize your tax strategy with precision.
          </p>
          <div className="grid grid-cols-3 gap-4 max-w-md">
            <div className="rounded-lg bg-gray-50 border border-gray-100 p-4 text-center">
              <TrendingUp className="h-6 w-6 mx-auto mb-2" style={{ color: '#2196F3' }} />
              <p className="text-xs font-medium" style={{ color: '#2D4B6E' }}>Multi-Scenario</p>
            </div>
            <div className="rounded-lg bg-gray-50 border border-gray-100 p-4 text-center">
              <BarChart3 className="h-6 w-6 mx-auto mb-2" style={{ color: '#2196F3' }} />
              <p className="text-xs font-medium" style={{ color: '#2D4B6E' }}>Visual Compare</p>
            </div>
            <div className="rounded-lg bg-gray-50 border border-gray-100 p-4 text-center">
              <Shield className="h-6 w-6 mx-auto mb-2" style={{ color: '#2196F3' }} />
              <p className="text-xs font-medium" style={{ color: '#2D4B6E' }}>Secure</p>
            </div>
          </div>
        </div>

        <p className="text-gray-300 text-xs">© 2026 Tactical Tax Genie. All rights reserved.</p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <Card className="w-full max-w-md border-0 shadow-none lg:shadow-none">
          <CardHeader className="text-center space-y-3 lg:text-left">
            {/* Mobile logo — sharp corners */}
            <div className="mx-auto lg:mx-0 lg:hidden">
              <img src={tacticalLogo} alt="Tactical Tax Genie" className="h-12 w-12 object-contain" style={{ borderRadius: 0 }} />
            </div>
            <CardTitle className="text-2xl" style={{ color: '#2D4B6E' }}>
              {mode === "login" ? "Welcome back" : "Reset password"}
            </CardTitle>
            <CardDescription>
              {mode === "login" ?
              "Sign in to access your clients" :
              "Enter your email to receive a reset link"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={mode === "login" ? handleLogin : handleForgot}
              className="space-y-4">
              
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="h-11" />
                
              </div>
              {mode === "login" &&
              <div className="space-y-2">
                  <Label>Password</Label>
                  <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={12}
                  className="h-11" />
                
                </div>
              }
              <Button type="submit" className="w-full h-11" disabled={loading}>
                {loading ?
                "Please wait…" :
                mode === "login" ?
                "Sign In" :
                "Send Reset Link"}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-muted-foreground space-y-1">
              {mode === "login" &&
              <button type="button" className="underline hover:text-foreground" onClick={() => setMode("forgot")}>
                  Forgot password?
                </button>
              }
              {mode === "forgot" &&
              <p>
                  Back to{" "}
                  <button
                  type="button"
                  className="underline hover:text-foreground font-medium"
                  onClick={() => setMode("login")}>
                  
                    sign in
                  </button>
                </p>
              }
            </div>
          </CardContent>
        </Card>
      </div>
    </div>);

}